import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { computeApplicationFee, readStoredZohoData } from '../utils/applicationFee';

// Fetches the latest Zoho CRM data (grade/batch can change after login — e.g.
// once Stage 1 is filled) and derives the application fee from it. Used by
// the dashboard, payment page, and application review page so all three
// always agree on the fee instead of each trusting whatever stale zohoData
// happened to be cached at login time.
export const useApplicationFee = () => {
  const { user, updateUser } = useAuth();
  const [zohoData, setZohoData] = useState(() => user?.zohoData || readStoredZohoData());
  const [feeDataLoading, setFeeDataLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const refreshZohoData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setFeeDataLoading(false);
        return;
      }

      try {
        const response = await axios.post('/api/auth/refresh-zoho-data', {}, {
          headers: { 'x-auth-token': token }
        });

        if (!cancelled && response.data?.zohoData) {
          const dataWithTimestamp = { ...response.data.zohoData, lastUpdated: Date.now() };
          localStorage.setItem('zohoCRMData', JSON.stringify(dataWithTimestamp));
          setZohoData(dataWithTimestamp);
          updateUser({ zohoData: dataWithTimestamp });
        }
      } catch (error) {
        console.warn('⚠️ useApplicationFee: Zoho data refresh failed, using cached data:', error.message);
        if (!cancelled) {
          setZohoData(prev => prev || user?.zohoData || readStoredZohoData());
        }
      } finally {
        if (!cancelled) setFeeDataLoading(false);
      }
    };

    refreshZohoData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ...computeApplicationFee(zohoData), feeDataLoading };
};
